import type { ReactNode } from 'react'
import { useT } from '../lib/i18n'

type Tone = 'ink' | 'protein' | 'carbs' | 'fat' | 'fiber'

interface RingProps {
  value: number
  target: number
  size?: number
  tone?: Tone
  /** The big figure in the middle. Defaults to the remaining amount. */
  center?: ReactNode
  caption?: string
  /** Read to a screen reader instead of the arc, which is unreadable. */
  ariaLabel?: string
}

const TONE_STROKE: Record<Tone, string> = {
  ink: 'stroke-ink',
  protein: 'stroke-protein',
  carbs: 'stroke-carbs',
  fat: 'stroke-fat',
  fiber: 'stroke-fiber',
}

/**
 * An arc gauge.
 *
 * **Over target draws outward, it never breaks the ring.** A progress ring that
 * wraps around and overwrites itself loses the one thing it was showing — how
 * far past the target you are — and a ring that clips at 100 % says "done" when
 * the truth is "180 %". So at ≤100 % there is one arc; past it the inner ring
 * completes and the excess is drawn as a second, thinner arc on a **larger
 * radius** in `--over`. That is the design system's rule for `CalorieRing`
 * (EPIC-06 builds it on this), and this ticket's acceptance criterion.
 *
 * The overshoot arc is capped at one full outer turn: past 200 % the geometry
 * stops being informative, and the number in the middle is doing the work by
 * then. The cap is visible in the `/ui-kit` route at 320 %.
 *
 * An SVG arc says nothing to a screen reader, so the whole figure carries
 * `role="img"` and a label with the actual numbers in it.
 *
 * SUAP-0103.
 */
export function Ring({
  value,
  target,
  size = 160,
  tone = 'ink',
  center,
  caption,
  ariaLabel,
}: RingProps) {
  const { t } = useT()
  const stroke = Math.max(8, Math.round(size * 0.075))
  const radius = (size - stroke) / 2
  const outerRadius = radius + stroke * 0.9
  const circumference = 2 * Math.PI * radius
  const outerCircumference = 2 * Math.PI * outerRadius

  const fraction = target > 0 ? value / target : 0
  const primary = Math.min(1, Math.max(0, fraction))
  const overshoot = Math.min(1, Math.max(0, fraction - 1))
  const remaining = target - value

  // The outer arc needs room, so the viewBox is wider than the ring itself.
  const box = size + stroke * 2

  return (
    // A <div role="img">, not a <figure role="img">: axe flags the latter,
    // because `figure` already has an implicit role that `img` is not allowed to
    // override. Found by the axe pass in this ticket's verification. The label
    // carries the numbers, since an arc says nothing to a screen reader.
    <div
      role="img"
      aria-label={
        ariaLabel ??
        t('ring.ofTarget', { value: String(value), target: String(target) }) +
          (overshoot > 0
            ? t('ring.overTarget', { percent: String(Math.round((fraction - 1) * 100)) })
            : '')
      }
      className="relative inline-flex items-center justify-center"
      style={{ width: box, height: box }}
    >
      <svg width={box} height={box} viewBox={`0 0 ${box} ${box}`} aria-hidden>
        {/* -90° so zero is at twelve o'clock, which is where a person looks first. */}
        <g transform={`rotate(-90 ${box / 2} ${box / 2})`}>
          <circle
            cx={box / 2}
            cy={box / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            className="stroke-hairline"
          />
          <circle
            cx={box / 2}
            cy={box / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - primary)}
            className={`${TONE_STROKE[tone]} motion-safe:transition-[stroke-dashoffset] motion-safe:duration-500`}
          />
          {overshoot > 0 ? (
            <circle
              cx={box / 2}
              cy={box / 2}
              r={outerRadius}
              fill="none"
              strokeWidth={stroke * 0.5}
              strokeLinecap="round"
              strokeDasharray={outerCircumference}
              strokeDashoffset={outerCircumference * (1 - overshoot)}
              className="stroke-over motion-safe:transition-[stroke-dashoffset] motion-safe:duration-500"
            />
          ) : null}
        </g>
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-center">
        <span className="text-3xl font-bold tabular-nums text-ink">
          {center ?? Math.round(remaining)}
        </span>
        {caption === undefined ? null : <span className="text-xs text-ink-soft">{caption}</span>}
      </div>
    </div>
  )
}
